import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import useApiClient from '../../../hooks/api/useApiClient';
import {
  calculateTotals,
  countAndPercentageLabelFormatter,
  generateMonthRange,
  getCompanyCountsMap,
  getCompanyIdsMap,
  getPeriodsMap,
} from './chartHelpers';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

export default function SalesTiersChart({ monthRange, salesManagers, onBarSelect }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:CustomerBase:customer_health_tab' });

  const [, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [totals, setTotals] = useState(null);

  const { CustomerBaseStatsManager } = useApiClient();

  useEffect(() => {
    fetchData();
  }, [monthRange, salesManagers]);

  const fetchData = async () => {
    setLoading(true);
    const { data, success } = await CustomerBaseStatsManager.getSalesTiersStats({
      start_date: monthRange[0].format('YYYY-MM-DD'),
      end_date: monthRange[1].format('YYYY-MM-DD'),
      client_timezone: moment.tz.guess(),
      managers: salesManagers,
    });
    setLoading(false);

    if (!success) {
      return toast.error(t('toasts.failed_to_load_data'));
    }

    setTotals(calculateTotals(data));
    setData(data);
  };

  const chartData = {
    labels: data
      ? Object.keys(data['0_699']).map((date) => moment(date, 'YYYY-MM').format('MMM YYYY'))
      : generateMonthRange(monthRange),
    datasets: [
      {
        label: t('dataset_labels.negative', { values: '0 - 699' }),
        data: getCompanyCountsMap(data, '0_699'),
        backgroundColor: 'rgba(255, 99, 132, 0.7)',
        meta: {
          companyIds: getCompanyIdsMap(data, '0_699'),
          periods: getPeriodsMap(data, '0_699'),
        },
      },
      {
        label: t('dataset_labels.ok', { values: '700 - 2499' }),
        data: getCompanyCountsMap(data, '700_2499'),
        backgroundColor: 'rgba(255, 206, 86, 0.7)',
        meta: {
          companyIds: getCompanyIdsMap(data, '700_2499'),
          periods: getPeriodsMap(data, '700_2499'),
        },
      },
      {
        label: t('dataset_labels.good', { values: '2500 - 7499' }),
        data: getCompanyCountsMap(data, '2500_7499'),
        backgroundColor: 'rgba(75, 192, 192, 0.7)',
        meta: {
          companyIds: getCompanyIdsMap(data, '2500_7499'),
          periods: getPeriodsMap(data, '2500_7499'),
        },
      },
      {
        label: t('dataset_labels.excellent', { values: '7500+' }),
        data: getCompanyCountsMap(data, '7500'),
        backgroundColor: 'rgba(54, 162, 235, 0.7)',
        meta: {
          companyIds: getCompanyIdsMap(data, '7500'),
          periods: getPeriodsMap(data, '7500'),
        },
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      title: {
        display: true,
        text: t('titles.sales_tiers'),
      },
      datalabels: {
        formatter: (value, context) => countAndPercentageLabelFormatter(value, totals[context.dataIndex]),
      },
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
      },
    },
    onClick: (event, elements) => {
      if (elements.length < 1) {
        return;
      }

      const { datasetIndex } = elements[0];
      const barIndex = elements[0].index;

      onBarSelect({
        companyIds: chartData.datasets[datasetIndex].meta.companyIds[barIndex],
        period: chartData.datasets[datasetIndex].meta.periods[barIndex],
      });
    },
  };

  return <Bar data={chartData} options={chartOptions} />;
}
